import { Portfolio } from "@/schemas/portfolio"; // Use Zod-inferred type
import { Business } from "@/schemas/business";   // Use Zod-inferred type

/**
 * Checks if a portfolio contains the current member
 */
export const portfolioContainsMember = (
  portfolio: Portfolio,
  currentUserId: string | undefined
): boolean => {
  if (!currentUserId || !portfolio.members) return false

  return portfolio.members.some((member) => member.userId === currentUserId)
}

/**
 * Checks if a business matches the status and industry filters
 */
export const businessMatchesFilters = (
  business: Business,
  statusFilters: string[],
  industryFilters: string[]
): boolean => {
  // Status filter
  const statusMatch =
    statusFilters.length === 0 || statusFilters.includes(business.status)

  // Industry filter
  const industryMatch =
    industryFilters.length === 0 ||
    (business.industry && industryFilters.includes(business.industry))

  return statusMatch && industryMatch
}

/**
 * Checks if a portfolio matches the name filter
 */
export const portfolioMatchesNameFilter = (
  portfolio: Portfolio,
  portfolioFilters: string[]
): boolean => {
  return (
    portfolioFilters.length === 0 || portfolioFilters.includes(portfolio.name) // Changed portfolio.title to portfolio.name
  )
}

/**
 * Process a portfolio to determine if it should be included in results based on all filters
 */
export const processPortfolio = (
  portfolio: Portfolio,
  allBusinesses: Business[], // Added allBusinesses parameter
  portfolioFilters: string[],
  statusFilters: string[],
  industryFilters: string[],
  currentUserId?: string,
  filterByMember: boolean = false
): Portfolio | null => { // Return type is still Portfolio | null
  // Filter by member if requested
  if (filterByMember && !portfolioContainsMember(portfolio, currentUserId)) {
    return null
  }

  // Portfolio name filter
  if (!portfolioMatchesNameFilter(portfolio, portfolioFilters)) {
    return null
  }

  // If there are no status or industry filters, include the portfolio as is
  if (statusFilters.length === 0 && industryFilters.length === 0) {
    return portfolio
  }

  // If there are no status or industry filters, include the portfolio as is
  if (statusFilters.length === 0 && industryFilters.length === 0) {
    return portfolio;
  }

  // Resolve businesses for this portfolio using businessRefs
  const portfolioBusinessObjects = (portfolio.businessRefs || [])
    .map(ref => allBusinesses.find(b => b.id === ref))
    .filter(b => b !== undefined) as Business[];

  // If the portfolio has no associated businesses (after resolving refs),
  // only include it if no business-specific filters (status, industry) are active.
  if (portfolioBusinessObjects.length === 0) {
    return (statusFilters.length === 0 && industryFilters.length === 0)
      ? portfolio
      : null;
  }

  // Filter these resolved business objects
  const matchingBusinessObjects = portfolioBusinessObjects.filter((business) =>
    businessMatchesFilters(business, statusFilters, industryFilters)
  );

  // If no businesses match the criteria after filtering, filter out this portfolio
  if (matchingBusinessObjects.length === 0) {
    return null;
  }

  // The portfolio itself matches. Return the original portfolio object.
  // We don't add a 'businesses' field with filtered objects to the Portfolio,
  // as the Portfolio schema has 'businessRefs'.
  // The calling context (e.g., UI) can use the same filtering logic if it needs to display
  // only the matching businesses for a portfolio that passes the filter.
  return portfolio; 
};

/**
 * Filter portfolios based on current filters
 */
export const filterPortfolios = (
  portfolios: Portfolio[],
  allBusinesses: Business[], // Added allBusinesses parameter
  portfolioFilters: string[],
  statusFilters: string[],
  industryFilters: string[],
  currentUserId?: string,
  filterByMember: boolean = false
): Portfolio[] => {
  return portfolios
    .map((portfolio) =>
      processPortfolio(
        portfolio,
        allBusinesses, // Pass allBusinesses
        portfolioFilters,
        statusFilters,
        industryFilters,
        currentUserId,
        filterByMember
      )
    )
    .filter(Boolean) as Portfolio[];
};
